import { useEffect, useState } from 'react';
import axios from 'axios';
import { ScrollText, RefreshCw, RefreshCcw, Plus, Minus, Gift, Trash2, Settings as SettingsIcon, KeyRound, Shield, DollarSign, Tag, Calendar, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Friendly mapping: action key -> { label, icon, color }
const ACTION_META = {
    credit_deposit:           { label: 'Credit Deposit',          icon: Plus,         color: '#39FF14' },
    credit_withdraw:          { label: 'Credit Withdraw',         icon: Minus,        color: '#FF3B30' },
    free_spins_grant:         { label: 'Free Spins Granted',      icon: Star,         color: '#FF007F' },
    promotion:                { label: 'Free Play / Promo',       icon: Gift,         color: '#FFD700' },
    promo_code_create:        { label: 'Promo Code Created',      icon: Tag,          color: '#FFD700' },
    promo_code_delete:        { label: 'Promo Code Deleted',      icon: Trash2,       color: '#FF3B30' },
    promo_code_toggle:        { label: 'Promo Code Toggled',      icon: Tag,          color: '#FFD700' },
    promotion_scheduled_fired:{ label: 'Scheduled Promo Fired',   icon: Calendar,     color: '#3B82F6' },
    winback_trigger_fired:    { label: 'Win-back Trigger Fired',  icon: Calendar,     color: '#A855F7' },
    weekly_cashback_processed:{ label: 'Weekly Cashback Run',     icon: DollarSign,   color: '#00FFFF' },
    user_status_change:       { label: 'User Status Changed',     icon: Shield,       color: '#A0AAB2' },
    user_password_reset:      { label: 'Password Reset',          icon: KeyRound,     color: '#00BFFF' },
    customer_created:         { label: 'Customer Created',        icon: Plus,         color: '#39FF14' },
    settings_update:          { label: 'Settings Updated',        icon: SettingsIcon, color: '#FFD700' },
    cashout_approved:         { label: 'Cashout Approved',        icon: DollarSign,   color: '#39FF14' },
    cashout_rejected:         { label: 'Cashout Rejected',        icon: DollarSign,   color: '#FF3B30' },
    cashout_marked_paid:      { label: 'Cashout Paid (cash)',     icon: DollarSign,   color: '#39FF14' },
    cashout_played_back:      { label: 'Cashout Played Back',     icon: RefreshCcw,   color: '#00E0FF' },
    test_data_wipe:           { label: 'Test Data Wiped',         icon: Trash2,       color: '#FF3B30' },
    wipe_test_data:           { label: 'Test Data Wiped',         icon: Trash2,       color: '#FF3B30' },
};

// Where each action navigates to when clicked
const NAV_MAP = {
    credit_deposit:           { section: 'players',    tab: 'users',     useTargetEmail: true },
    credit_withdraw:          { section: 'players',    tab: 'users',     useTargetEmail: true },
    free_spins_grant:         { section: 'players',    tab: 'users',     useTargetEmail: true },
    user_status_change:       { section: 'players',    tab: 'users',     useTargetEmail: true },
    user_password_reset:      { section: 'players',    tab: 'users',     useTargetEmail: true },
    customer_created:         { section: 'players',    tab: 'users',     useTargetEmail: true },
    cashout_approved:         { section: 'players',    tab: 'cashouts' },
    cashout_rejected:         { section: 'players',    tab: 'cashouts' },
    cashout_marked_paid:      { section: 'players',    tab: 'cashouts' },
    cashout_played_back:      { section: 'players',    tab: 'cashouts' },
    promotion:                { section: 'promotions', tab: 'freeplay' },
    promo_code_create:        { section: 'promotions', tab: 'promos' },
    promo_code_delete:        { section: 'promotions', tab: 'promos' },
    promo_code_toggle:        { section: 'promotions', tab: 'promos' },
    promotion_scheduled_fired:{ section: 'promotions', tab: 'promo-perf' },
    winback_trigger_fired:    { section: 'promotions', tab: 'promo-perf' },
    weekly_cashback_processed:{ section: 'promotions', tab: 'cashback' },
};

// Filter category for each action — used by the chip row
const CATEGORY_MAP = {
    credit_deposit: 'loads', credit_withdraw: 'loads',
    promotion: 'promos', promo_code_create: 'promos', promo_code_delete: 'promos',
    promo_code_toggle: 'promos', promotion_scheduled_fired: 'promos',
    winback_trigger_fired: 'promos', weekly_cashback_processed: 'promos',
    free_spins_grant: 'promos',
    cashout_approved: 'cashouts', cashout_rejected: 'cashouts',
    cashout_marked_paid: 'cashouts', cashout_played_back: 'cashouts',
    settings_update: 'settings',
    user_status_change: 'other', user_password_reset: 'other',
    customer_created: 'other', test_data_wipe: 'other', wipe_test_data: 'other',
};

const FILTERS = [
    { id: 'all',      label: 'All' },
    { id: 'loads',    label: 'Loads' },
    { id: 'promos',   label: 'Promos' },
    { id: 'cashouts', label: 'Cashouts' },
    { id: 'settings', label: 'Settings' },
    { id: 'other',    label: 'Other' },
];

const fmtCT = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
    }) + ' CT';
};

const summarizeDetails = (action, details, log = {}) => {
    if (!details || typeof details !== 'object') details = {};
    const target = log.target_email || details.target_email || details.email || details.user_email || '';
    if (action === 'credit_deposit' || action === 'credit_withdraw') {
        return `$${(details.amount || 0).toFixed(2)}${target ? ` → ${target}` : ''}`;
    }
    if (action === 'promotion') {
        return `$${(details.amount || 0).toFixed(2)} × ${details.recipients || 0} (${details.reason || ''})`;
    }
    if (action === 'promotion_scheduled_fired' || action === 'winback_trigger_fired') {
        return `${details.name || ''}: $${(details.amount || 0).toFixed(2)} × ${details.recipients || 0}`;
    }
    if (action === 'weekly_cashback_processed') {
        return `$${(details.total_cashback || 0).toFixed(2)} to ${details.users_processed || 0} users`;
    }
    if (action === 'free_spins_grant') {
        return `${details.amount || 0} spins${target ? ` → ${target}` : ''}`;
    }
    if (action === 'user_status_change') {
        return `${target} → ${details.new_status || ''}`;
    }
    if (action === 'customer_created') {
        return target || details.email || '';
    }
    if (action === 'cashout_approved' || action === 'cashout_rejected' ||
        action === 'cashout_marked_paid' || action === 'cashout_played_back') {
        const amt = Math.abs(Number(details.amount || 0)).toFixed(2);
        return `$${amt}${target ? ` — ${target}` : ''}`;
    }
    if (action === 'test_data_wipe' || action === 'wipe_test_data') {
        return `Cleared ${details.collections_cleared || 0} collections`;
    }
    return target || '';
};

export const AdminActivityLog = ({ onNavigate }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/audit-logs?limit=50`, { withCredentials: true });
            setLogs(Array.isArray(data) ? data : []);
        } catch {
            // silent — UI shows empty state
        }
        setLoading(false);
    };

    useEffect(() => { fetchLogs(); }, []);

    // Apply filter (client-side; we fetch a slightly larger window of 50 so filtering still has data to show)
    const visibleLogs = (filter === 'all'
        ? logs
        : logs.filter(l => (CATEGORY_MAP[l.action] || 'other') === filter)
    ).slice(0, 20);

    // Counts per filter for the chip badges
    const counts = logs.reduce((acc, l) => {
        const cat = CATEGORY_MAP[l.action] || 'other';
        acc[cat] = (acc[cat] || 0) + 1;
        acc.all = (acc.all || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="card-dark p-6" data-testid="admin-activity-log">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <ScrollText className="w-5 h-5 text-[#FFD700]" />
                    Recent Admin Activity
                </h3>
                <Button
                    size="sm" variant="outline" onClick={fetchLogs}
                    className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10"
                    data-testid="admin-activity-refresh"
                >
                    <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>
            <p className="text-[11px] text-[#A0AAB2] mb-3">
                Last 20 admin actions. Spot anything unusual at a glance.
                For full history, see <span className="font-bold text-[#FFD700]">Reports → Audit</span>.
            </p>

            {/* Filter chips */}
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="admin-activity-filters">
                {FILTERS.map(f => {
                    const c = counts[f.id] || 0;
                    const active = filter === f.id;
                    return (
                        <button
                            key={f.id}
                            type="button"
                            onClick={() => setFilter(f.id)}
                            className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border transition-colors flex items-center gap-1.5 ${
                                active
                                    ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                    : 'bg-[#0a0a0a] text-[#A0AAB2] border-white/10 hover:text-white hover:border-[#FFD700]/40'
                            }`}
                            data-testid={`admin-activity-filter-${f.id}`}
                        >
                            {f.label}
                            <span className={`px-1 rounded text-[9px] font-mono ${
                                active ? 'bg-black/20 text-black' : 'bg-white/10 text-[#A0AAB2]'
                            }`}>
                                {c}
                            </span>
                        </button>
                    );
                })}
            </div>

            {loading && logs.length === 0 ? (
                <div className="text-center py-6 text-[#A0AAB2] text-xs">Loading…</div>
            ) : visibleLogs.length === 0 ? (
                <div className="text-center py-6 text-[#A0AAB2] text-xs" data-testid="admin-activity-empty">
                    {logs.length === 0 ? 'No admin activity yet.' : `No ${filter} activity in the last 50 actions.`}
                </div>
            ) : (
                <div className="space-y-2">
                    {visibleLogs.map((log, i) => {
                        const meta = ACTION_META[log.action] || { label: log.action, icon: ScrollText, color: '#A0AAB2' };
                        const Icon = meta.icon;
                        const summary = summarizeDetails(log.action, log.details, log);
                        const nav = NAV_MAP[log.action];
                        const clickable = !!(onNavigate && nav);
                        const targetEmail = log.target_email || log.details?.target_email || log.details?.email || log.details?.user_email || '';
                        const handleClick = () => {
                            if (!clickable) return;
                            onNavigate({
                                section: nav.section,
                                tab: nav.tab,
                                search: nav.useTargetEmail && targetEmail ? targetEmail : undefined,
                            });
                        };
                        return (
                            <div
                                key={i}
                                role={clickable ? 'button' : undefined}
                                tabIndex={clickable ? 0 : undefined}
                                onClick={handleClick}
                                onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleClick(); } }}
                                className={`flex items-start gap-3 p-2.5 rounded-lg bg-[#0a0a0a] border border-white/5 transition-colors ${
                                    clickable ? 'cursor-pointer hover:border-[#FFD700]/40 hover:bg-white/5' : 'hover:border-white/10'
                                }`}
                                data-testid={`admin-activity-row-${i}`}
                                title={clickable ? `Open ${nav.section} → ${nav.tab}${nav.useTargetEmail && targetEmail ? ` (${targetEmail})` : ''}` : undefined}
                            >
                                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                                    style={{ backgroundColor: meta.color + '22' }}>
                                    <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="text-sm font-bold" style={{ color: meta.color }}>{meta.label}</span>
                                        <span className="text-[10px] text-[#A0AAB2] font-mono">{fmtCT(log.created_at)}</span>
                                    </div>
                                    {summary && (
                                        <p className="text-xs text-white/80 truncate mt-0.5">{summary}</p>
                                    )}
                                    {!summary && log.admin_email && (
                                        <p className="text-xs text-[#A0AAB2] truncate mt-0.5">by {log.admin_email}</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
